import { ActivityNavigationUtility } from "@b2bportal/core-utilities";
import {
  AvailabilitySort,
  DurationRefinement,
  StartTimeRefinement,
} from "@b2bportal/rbc-activities-api";
import { ACTIVITY_URL_PARAM_KEYS } from "@overrides/utilities";
import { ActivityRatingValueEnum } from "../features/availability/components/FilterRating/FilterRating";

/**
 * Validate enum value against allowed values
 */
const isValidEnumValue = <T extends Record<string, string>>(
  value: string,
  enumObj: T
): value is T[keyof T] => {
  return Object.values(enumObj).includes(value);
};

export type ActivitySearchQueryParamValues = {
  fromDate: string | null;
  untilDate: string | null;
  travelers: number | undefined;
  filters: {
    maxPrice: number | null;
    freeCancellation: boolean;
    rating: ActivityRatingValueEnum | null;
    duration: DurationRefinement[];
    startTime: StartTimeRefinement[];
    userRating: number | null;
  };
  sortBy: AvailabilitySort;
};

/**
 * Parse and validate activity URL parameters
 * Similar to hotels' parseLodgingParams utility
 */
export const parseActivityParams = (
  searchParams: URLSearchParams
): ActivitySearchQueryParamValues => {
  // Search params
  const fromDate = searchParams.get(ACTIVITY_URL_PARAM_KEYS.FROM_DATE);
  const untilDate = searchParams.get(ACTIVITY_URL_PARAM_KEYS.UNTIL_DATE);
  const travelersParam = searchParams.get(ACTIVITY_URL_PARAM_KEYS.ADULTS_COUNT);

  // Parse travelers with NaN check
  const parsedTravelers =
    travelersParam != null ? parseInt(travelersParam, 10) : NaN;
  const travelers = !isNaN(parsedTravelers) ? parsedTravelers : undefined;

  // Filter params
  const priceMax = searchParams.get(ACTIVITY_URL_PARAM_KEYS.PRICE_RANGE_MAX);
  const freeCancellation =
    searchParams.get(ACTIVITY_URL_PARAM_KEYS.FREE_CANCELLATION) === "true";
  const ratingParam = searchParams.get(ACTIVITY_URL_PARAM_KEYS.RATING);
  const durationParam = searchParams.get(ACTIVITY_URL_PARAM_KEYS.DURATION);
  const startTimeParam = searchParams.get(ACTIVITY_URL_PARAM_KEYS.START_TIME);
  const userRatingParam = searchParams.get(ACTIVITY_URL_PARAM_KEYS.USER_RATING);

  // Parse and validate price (check for NaN and bounds)
  const parsedPrice =
    priceMax != null && priceMax.length > 0 ? parseInt(priceMax, 10) : null;
  // Ensure price is a valid positive number within reasonable bounds (max $100k)
  const validPrice =
    parsedPrice != null &&
    !isNaN(parsedPrice) &&
    parsedPrice > 0 &&
    parsedPrice <= 100000
      ? parsedPrice
      : null;

  // Parse and validate user rating (check for NaN and bounds)
  const parsedUserRating =
    userRatingParam != null && userRatingParam.length > 0
      ? parseFloat(userRatingParam)
      : null;
  // User ratings should be between 0 and 5
  const validUserRating =
    parsedUserRating != null &&
    !isNaN(parsedUserRating) &&
    parsedUserRating >= 0 &&
    parsedUserRating <= 5
      ? parsedUserRating
      : null;

  // Validate rating enum
  const validRating =
    ratingParam != null &&
    ratingParam.length > 0 &&
    isValidEnumValue(ratingParam, ActivityRatingValueEnum)
      ? (ratingParam as ActivityRatingValueEnum)
      : null;

  // Validate duration enums
  const validDurations =
    durationParam != null && durationParam.length > 0
      ? (durationParam
          .split(",")
          .filter((d) =>
            isValidEnumValue(d, DurationRefinement)
          ) as DurationRefinement[])
      : [];

  // Validate start time enums
  const validStartTimes =
    startTimeParam != null && startTimeParam.length > 0
      ? (startTimeParam
          .split(",")
          .filter((s) =>
            isValidEnumValue(s, StartTimeRefinement)
          ) as StartTimeRefinement[])
      : [];

  // Sort param
  const sortBy = searchParams.get(ACTIVITY_URL_PARAM_KEYS.SORT_BY);
  const validSort =
    sortBy != null && isValidEnumValue(sortBy, AvailabilitySort)
      ? (sortBy as AvailabilitySort)
      : AvailabilitySort.Recommended;

  // Validate and format dates (YYYY-MM-DD) - prevents malformed strings from reaching API
  // Uses formatDateForUrl which validates and normalizes date strings
  const validFromDate =
    ActivityNavigationUtility.formatDateForUrl(fromDate) || null;
  const validUntilDate =
    ActivityNavigationUtility.formatDateForUrl(untilDate) || null;

  return {
    fromDate: validFromDate,
    untilDate: validUntilDate,
    travelers,
    filters: {
      maxPrice: validPrice,
      freeCancellation,
      rating: validRating,
      duration: validDurations,
      startTime: validStartTimes,
      userRating: validUserRating,
    },
    sortBy: validSort,
  };
};
